import React, { useEffect, useState } from 'react';
import * as vp from 'vopay';
import { useTranslation } from 'react-i18next';
import PaymentMethod from './PaymentMethod/PaymentMethod';
import { useToken } from '@/providers/Token.provider';
import { usePaylink } from '@/providers/Paylink.provider';
import { useFlexiblePayment } from '@/providers/FlexiblePayment.provider';

const { Box, Icon } = vp;

export type PaymentMethodsProps = {
  flexiblePaymentMethod: vp.FlexiblePaymentMethods;
  onClick: (connectionType: vp.ConnectionTypes) => void;
};

type PaymentMethodKey = 'bankAccount' | 'creditCard' | 'debitCard';

const PaymentMethods: React.FC<PaymentMethodsProps> = ({ flexiblePaymentMethod, onClick }) => {
  const { token } = useToken();
  const { t } = useTranslation('translations', { keyPrefix: 'MultiplePaymentMethods' });
  const [availableAmount, setAvailableAmount] = useState<number>(0);
  const [updatingKey, setUpdatingKey] = useState<PaymentMethodKey | null>(null);
  const { paylink } = usePaylink();
  const { updateFlexiblePayment, isFlexiblePayment, paymentMethods, removePaymentMethod, addPaymentMethod } =
    useFlexiblePayment();
  useEffect(() => {
    if (paylink) {
      const sumUsedAmount = paymentMethods.reduce((acc, method) => acc + method.amount, 0);
      setAvailableAmount(Number(vp.sanitizeToNumeric(paylink.Amount)) - sumUsedAmount);
    }
  }, [paylink, paymentMethods]);
  const [bankLogoSrc, setBankLogoSrc] = useState('');
  useEffect(() => {
    const bankMethod = paymentMethods.find((method) => method.method === vp.ConnectionTypes.Bank);
    const bankId = bankMethod?.FinancialInstitution?.FinancialInstitutionID;

    if (bankId && vp.hasBankImage(bankId.toString())) {
      import(`../../../../../assets/images/BankLogos/${bankId}.svg`)
        .then((image: { default: string | null }) => {
          if (image.default) setBankLogoSrc(image.default);
        })
        .catch((err) => {
          console.error('Error loading bank logo:', err);
        });
    }
  }, [paymentMethods]);

  useEffect(() => {
    if (isFlexiblePayment) {
      updateFlexiblePayment(false, flexiblePaymentMethod);
      paymentMethods
        .filter((payment) => !payment.token || payment.token === '')
        .forEach((payment) => {
          removePaymentMethod(payment.method);
        });
    }
  }, []);
  const updatingProps = (key: PaymentMethodKey) => ({
    onUpdatingChange: (updating: boolean) => setUpdatingKey(updating ? key : null),
    disabled: updatingKey !== null && updatingKey !== key,
  });
  const onUpdatePaymentMethod = (amount: string, connectionType: vp.ConnectionTypes) => {
    const paymentMethodFind = paymentMethods.find((method) => method.method === connectionType);
    if (paymentMethodFind) {
      paymentMethodFind.amount = Number(vp.sanitizeToNumeric(amount));
      addPaymentMethod(paymentMethodFind);
    }
  };
  return (
    <Box flx="col" base="8" gap="sm" pb="md" width="90%" height="auto">
      {String(token?.PaymentSelectionMethod) === 'any' ? (
        <>
          <PaymentMethod
            Header={
              <Box base="8" gap="sm">
                <Icon icon="multiBank" height="28px" width="28px" color="greyLight" /> <span>{t('bankAccount')}</span>
              </Box>
            }
            onClick={() => onClick(vp.ConnectionTypes.Bank)}
            amountRemaining={availableAmount}
            onRemove={() => {
              removePaymentMethod(vp.ConnectionTypes.Bank);
            }}
            onUpdate={(amount: string) => {
              onUpdatePaymentMethod(amount, vp.ConnectionTypes.Bank);
            }}
            paymentMethodKey="bankAccount"
            {...updatingProps('bankAccount')}
            isOpen={!!paymentMethods.find((method) => method.method === vp.ConnectionTypes.Bank)}
          />
          {flexiblePaymentMethod === vp.FlexiblePaymentMethods.MultiplePaymentMethods && (
            <PaymentMethod
              Header={
                <Box base="8" gap="sm">
                  <Icon icon="creditCard2" height="28px" width="28px" color="greyLight" />{' '}
                  <span>{t('creditCard')}</span>
                </Box>
              }
              onClick={() => onClick(vp.ConnectionTypes.Credit)}
              amountRemaining={availableAmount}
              onRemove={() => {
                removePaymentMethod(vp.ConnectionTypes.Credit);
              }}
              account={vp.getMaskedNumber(
                paymentMethods.find((method) => method.method === vp.ConnectionTypes.Credit)?.account || '',
                '**** ',
              )}
              onUpdate={(amount: string) => onUpdatePaymentMethod(amount, vp.ConnectionTypes.Credit)}
              paymentMethodKey="creditCard"
              {...updatingProps('creditCard')}
              amount={paymentMethods.find((method) => method.method === vp.ConnectionTypes.Credit)?.amount}
              isOpen={!!paymentMethods.find((method) => method.method === vp.ConnectionTypes.Credit)}
            />
          )}
          {flexiblePaymentMethod === vp.FlexiblePaymentMethods.MultiplePaymentMethods && (
            <PaymentMethod
              Header={
                <Box base="8" gap="sm">
                  <Icon icon="creditCard2" height="28px" width="28px" color="greyLight" /> <span>{t('debitCard')}</span>
                </Box>
              }
              onClick={() => onClick(vp.ConnectionTypes.DebitCard)}
              amountRemaining={availableAmount}
              onRemove={() => {
                removePaymentMethod(vp.ConnectionTypes.DebitCard);
              }}
              account={vp.getMaskedNumber(
                paymentMethods.find((method) => method.method === vp.ConnectionTypes.DebitCard)?.account || '',
                '**** ',
              )}
              onUpdate={(amount: string) => onUpdatePaymentMethod(amount, vp.ConnectionTypes.DebitCard)}
              paymentMethodKey="debitCard"
              {...updatingProps('debitCard')}
              amount={paymentMethods.find((method) => method.method === vp.ConnectionTypes.DebitCard)?.amount}
              isOpen={!!paymentMethods.find((method) => method.method === vp.ConnectionTypes.DebitCard)}
            />
          )}

          {/* {flexiblePaymentMethod === vp.FlexiblePaymentMethods.MultiplePaymentMethods && (
            <PaymentMethod
              Header={
                <Box base="8" gap="sm">
                  <Icon icon="interac2" height="28px" width="28px" color="greyLight" />
                  <InteracWordmark type={flow === 3 || flow === 4 ? 'moneyRequest' : 'eTransfer'} />
                </Box>
              }
              onClick={() => onClick(vp.ConnectionTypes.Email)}
              amountRemaining={availableAmount}
              onRemove={() => {
                removePaymentMethod(vp.ConnectionTypes.Email);
              }}
              account={vp.getMaskedNumber(
                paymentMethods.find((method) => method.method === vp.ConnectionTypes.Email)?.account || '',
              )}
              onUpdate={(amount: string) => onUpdatePaymentMethod(amount, vp.ConnectionTypes.Email)}
              paymentMethodKey="interac"
              amount={paymentMethods.find((method) => method.method === vp.ConnectionTypes.Email)?.amount}
              isOpen={!!paymentMethods.find((method) => method.method === vp.ConnectionTypes.Email)}
            />
          )} */}
        </>
      ) : (
        <>
          {String(token?.PaymentSelectionMethod).includes('bank') && (
            <PaymentMethod
              Header={
                <Box base="8" gap="sm" ai="center">
                  <Icon icon="multiBank" height="28px" width="28px" color="greyLight" /> <span>{t('bankAccount')}</span>
                </Box>
              }
              onClick={() => onClick(vp.ConnectionTypes.Bank)}
              paymentMethodKey="bankAccount"
              {...updatingProps('bankAccount')}
              amountRemaining={availableAmount}
              onRemove={() => {
                removePaymentMethod(vp.ConnectionTypes.Bank);
              }}
              onUpdate={(amount: string) => onUpdatePaymentMethod(amount, vp.ConnectionTypes.Bank)}
              account={
                <Box base="5" gap="sm" ai="center">
                  {vp.hasBankImage(
                    paymentMethods
                      .find((method) => method.method === vp.ConnectionTypes.Bank)
                      ?.FinancialInstitution?.FinancialInstitutionID.toString() || '',
                  ) && (
                    <img
                      style={{ maxWidth: '200px', maxHeight: '16px', width: '100%', height: '100%' }}
                      alt="Bank Logo"
                      src={bankLogoSrc}
                    />
                  )}
                  <span
                    style={{
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {vp.getMaskedNumber(
                      paymentMethods.find((method) => method.method === vp.ConnectionTypes.Bank)?.account || '',
                      '**** ',
                    )}
                  </span>
                </Box>
              }
              amount={paymentMethods.find((method) => method.method === vp.ConnectionTypes.Bank)?.amount}
              isOpen={!!paymentMethods.find((method) => method.method === vp.ConnectionTypes.Bank)}
            />
          )}
          {String(token?.PaymentSelectionMethod).includes('credit') &&
            flexiblePaymentMethod === vp.FlexiblePaymentMethods.MultiplePaymentMethods && (
              <PaymentMethod
                Header={
                  <Box base="8" gap="sm" ai="center">
                    <Icon icon="creditCard2" height="28px" width="28px" color="greyLight" />{' '}
                    <span>{t('creditCard')}</span>
                  </Box>
                }
                onClick={() => onClick(vp.ConnectionTypes.Credit)}
                paymentMethodKey="creditCard"
                {...updatingProps('creditCard')}
                amountRemaining={availableAmount}
                onUpdate={(amount: string) => onUpdatePaymentMethod(amount, vp.ConnectionTypes.Credit)}
                account={vp.getMaskedNumber(
                  paymentMethods.find((method) => method.method === vp.ConnectionTypes.Credit)?.account || '',
                  '**** ',
                )}
                onRemove={() => {
                  removePaymentMethod(vp.ConnectionTypes.Credit);
                }}
                amount={paymentMethods.find((method) => method.method === vp.ConnectionTypes.Credit)?.amount}
                isOpen={!!paymentMethods.find((method) => method.method === vp.ConnectionTypes.Credit)}
              />
            )}
          {String(token?.PaymentSelectionMethod).includes('debitcard') &&
            flexiblePaymentMethod === vp.FlexiblePaymentMethods.MultiplePaymentMethods && (
              <PaymentMethod
                Header={
                  <Box base="8" gap="sm" ai="center">
                    <Icon icon="creditCard2" height="28px" width="28px" color="greyLight" />{' '}
                    <span>{t('debitCard')}</span>
                  </Box>
                }
                onClick={() => onClick(vp.ConnectionTypes.DebitCard)}
                amountRemaining={availableAmount}
                onUpdate={(amount: string) => onUpdatePaymentMethod(amount, vp.ConnectionTypes.DebitCard)}
                account={vp.getMaskedNumber(
                  paymentMethods.find((method) => method.method === vp.ConnectionTypes.DebitCard)?.account || '',
                  '**** ',
                )}
                onRemove={() => {
                  removePaymentMethod(vp.ConnectionTypes.DebitCard);
                }}
                paymentMethodKey="debitCard"
                {...updatingProps('debitCard')}
                amount={paymentMethods.find((method) => method.method === vp.ConnectionTypes.DebitCard)?.amount}
                isOpen={!!paymentMethods.find((method) => method.method === vp.ConnectionTypes.DebitCard)}
              />
            )}
          {/* {String(token?.PaymentSelectionMethod).includes('email') &&
            flexiblePaymentMethod === vp.FlexiblePaymentMethods.MultiplePaymentMethods && (
              <PaymentMethod
                Header={
                  <Box base="8" gap="sm">
                    <Icon icon="interac2" height="28px" width="28px" color="none" />
                    <InteracWordmark type={flow === 3 || flow === 4 ? 'moneyRequest' : 'eTransfer'} />
                  </Box>
                }
                onClick={() => onClick(vp.ConnectionTypes.Email)}
                amountRemaining={availableAmount}
                onRemove={() => {
                  removePaymentMethod(vp.ConnectionTypes.Email);
                }}
                account={vp.getMaskedNumber(
                  paymentMethods.find((method) => method.method === vp.ConnectionTypes.Email)?.account || '',
                )}
                onUpdate={(amount: string) => onUpdatePaymentMethod(amount, vp.ConnectionTypes.Email)}
                paymentMethodKey="interac"
                amount={paymentMethods.find((method) => method.method === vp.ConnectionTypes.Email)?.amount}
                isOpen={!!paymentMethods.find((method) => method.method === vp.ConnectionTypes.Email)}
              />
            )} */}
        </>
      )}
    </Box>
  );
};

export default PaymentMethods;
